import { useEffect, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  downloadTextFile,
  formatRelativeTime,
  newIdempotencyId,
  parseConvexError,
} from "../lib/utils";
import { hasConvexConfig, hasWorkOsConfig, getWorkOsClientId } from "../lib/config";
import { WORKOS_JWT_TEMPLATE_DASHBOARD, workOsJwtTemplateJson } from "../lib/workos-jwt";

const APP_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://track-afk.pages.dev";

function inviteLink(token: string) {
  return `${APP_ORIGIN}/invite/${token}`;
}

export function AdminPage() {
  if (!hasConvexConfig) {
    return (
      <div className="page admin-page">
        <h1>Admin</h1>
        <p className="lede">
          Set <code>VITE_CONVEX_URL</code> and WorkOS credentials to manage membership, domains, and
          backups. See <code>docs/operations.md</code>.
        </p>
        {!hasWorkOsConfig && (
          <div className="banner">
            WorkOS is not configured (<code>VITE_WORKOS_CLIENT_ID</code>).
          </div>
        )}
      </div>
    );
  }
  return <AdminAuthenticated />;
}

function AdminAuthenticated() {
  const me = useQuery(api.organizations.me);
  const isAdmin = me?.role === "admin";
  const members = useQuery(api.organizations.listMembers, isAdmin ? {} : "skip");
  const openInvites = useQuery(api.organizations.listOpenInvites, isAdmin ? {} : "skip");
  const domains = useQuery(api.organizations.listAllowedDomains, isAdmin ? {} : "skip");
  const accessAttempts = useQuery(api.organizations.listAccessAttempts, isAdmin ? {} : "skip");
  const userAudit = useQuery(api.organizations.listUserAudit, isAdmin ? { limit: 40 } : "skip");
  const jwtProbe = useQuery(api.organizations.probeJwtClaims, isAdmin ? {} : "skip");
  const backup = useQuery(api.exports.fullBackupBundle, isAdmin ? {} : "skip");
  const addDomain = useMutation(api.organizations.addAllowedDomain);
  const removeDomain = useMutation(api.organizations.removeAllowedDomain);
  const editDomain = useMutation(api.organizations.editAllowedDomain);
  const inviteMember = useAction(api.workosActions.inviteMember);
  const integrationStatus = useAction(api.workosActions.integrationStatus);
  const revokeInvite = useMutation(api.organizations.revokeInvite);
  const approve = useMutation(api.organizations.approveMember);
  const reject = useMutation(api.organizations.rejectMember);
  const updateRole = useMutation(api.organizations.updateMemberRole);
  const removeMember = useMutation(api.organizations.removeMember);
  const dismissAttempt = useMutation(api.organizations.dismissAccessAttempt);
  const seed = useMutation(api.bootstrap.seed);

  const [domain, setDomain] = useState("");
  const [editingDomainId, setEditingDomainId] = useState<Id<"allowedDomains"> | null>(null);
  const [editingDomainValue, setEditingDomainValue] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "editor" | "viewer">("viewer");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);
  const [workOsApi, setWorkOsApi] = useState<{
    canSendInviteEmail: boolean;
    workOsApiConfigured: boolean;
  } | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    void integrationStatus({})
      .then((status) => {
        if (!active) return;
        setWorkOsApi({
          canSendInviteEmail: status.canSendInviteEmail,
          workOsApiConfigured: status.workOsApiConfigured,
        });
      })
      .catch(() => {
        if (active) setWorkOsApi(null);
      });
    return () => {
      active = false;
    };
  }, [isAdmin, integrationStatus]);

  if (me === undefined) {
    return (
      <div className="page admin-page">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="page admin-page">
        <h1>Admin</h1>
        <p className="lede">Admin role required.</p>
      </div>
    );
  }

  const pending = (members ?? []).filter((m) => m.status === "pending");
  const pendingApprovals = pending.filter((m) => m.source !== "invite");
  const active = (members ?? []).filter((m) => m.status === "active");
  const rejected = (members ?? []).filter((m) => m.status === "rejected");

  async function copyText(text: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      setMessage(successMessage);
      setError(null);
    } catch {
      setError("Could not copy. Select and copy the link manually.");
    }
  }

  async function sendInvite(email: string, role: "admin" | "editor" | "viewer") {
    setError(null);
    setLastInviteLink(null);
    const result = await inviteMember({
      email,
      name: email.split("@")[0] ?? email,
      role,
      idempotencyId: newIdempotencyId("invite"),
    });
    const link = inviteLink(result.token);
    setLastInviteLink(link);
    setInviteEmail("");
    if (result.emailSent) {
      setMessage(`Invite created for ${result.email}. WorkOS sent an invitation email.`);
    } else if (result.workosError) {
      setMessage(
        `Invite created for ${result.email}, but WorkOS email failed (${result.workosError}). Share the invite link below.`,
      );
    } else if (!result.workOsApiConfigured) {
      setMessage(
        `Invite created for ${result.email}. WORKOS_API_KEY is not set — copy and share the invite link below.`,
      );
    } else {
      setMessage(`Invite created for ${result.email}. Share the invite link below.`);
    }
  }

  return (
    <div className="page admin-page">
      <h1>Admin</h1>
      <p className="lede">
        Invite and approve users, manage roles and domains, and review access audit logs.
      </p>

      {!hasWorkOsConfig && (
        <div className="banner">
          WorkOS is not configured. AuthKit sign-in is disabled until{" "}
          <code>VITE_WORKOS_CLIENT_ID</code> is set.
        </div>
      )}

      {workOsApi && (
        <p className="muted">
          WorkOS API key:{" "}
          {workOsApi.canSendInviteEmail
            ? "configured — invite emails can be sent via WorkOS."
            : "not set — admins must copy and share invite links (no email)."}
        </p>
      )}

      <section className="order-section jwt-setup">
        <h2>WorkOS JWT setup (required)</h2>
        <p className="muted">
          Domain join and invite accept need a verified <code>email</code> claim on the access
          token. Also set <code>aud</code> to your WorkOS Client ID so Convex can validate the
          token. After saving the template, <strong>sign out and sign in again</strong>.
        </p>
        <ol className="access-steps">
          <li>
            Open{" "}
            <a href={WORKOS_JWT_TEMPLATE_DASHBOARD} target="_blank" rel="noreferrer">
              WorkOS → Authentication → Features → JWT Template
            </a>
          </li>
          <li>
            Paste the JSON below (Client ID:{" "}
            <code>{getWorkOsClientId() || "set VITE_WORKOS_CLIENT_ID"}</code>)
          </li>
          <li>Save, then sign out of Track and sign in with Google again</li>
          <li>Confirm the live probe below shows an email claim</li>
        </ol>
        <pre className="jwt-template-block">{workOsJwtTemplateJson()}</pre>
        <div className="toolbar">
          <button
            type="button"
            className="btn primary"
            onClick={() =>
              void copyText(workOsJwtTemplateJson(), "JWT template copied. Paste it in WorkOS.")
            }
          >
            Copy JWT template
          </button>
          <a className="btn" href={WORKOS_JWT_TEMPLATE_DASHBOARD} target="_blank" rel="noreferrer">
            Open WorkOS dashboard
          </a>
        </div>
        {jwtProbe === undefined ? (
          <p className="muted">Checking your current token…</p>
        ) : jwtProbe.authenticated ? (
          <div
            className={`banner ${jwtProbe.hasEmailClaim ? "ok-banner" : "conflict"}`}
            role="status"
          >
            {jwtProbe.hasEmailClaim ? (
              <>
                JWT OK — subject <code>{jwtProbe.subject}</code>, email{" "}
                <code>{jwtProbe.email}</code>
                {jwtProbe.emailVerifiedClaim === false ? " (email_verified=false)" : ""}
              </>
            ) : (
              <>
                JWT missing email claim. Claims seen:{" "}
                <code>{jwtProbe.claimKeys?.join(", ") || "(none)"}</code>. Paste the template, save,
                then sign out/in.
              </>
            )}
          </div>
        ) : (
          <div className="banner conflict" role="status">
            {jwtProbe.message}
          </div>
        )}
      </section>

      <section className="order-section">
        <h2>How access works</h2>
        <ol className="access-steps">
          <li>Add approved domains.</li>
          <li>Domain allowlist sign-in → Pending approvals → Approve/Deny.</li>
          <li>
            Admin invite → share invite link (or WorkOS email if API key set) → user signs in with
            Google as that email → auto-approved.
          </li>
          <li>Canonical user id is WorkOS user id (JWT subject).</li>
        </ol>
      </section>

      <section className="order-section">
        <h2>Pending approvals</h2>
        <p className="muted">
          Domain join requests. Approve requires the email domain to be allowed.
        </p>
        {pendingApprovals.length === 0 ? (
          <p className="muted">No pending approvals.</p>
        ) : (
          <ul className="order-list">
            {pendingApprovals.map((m) => (
              <li key={m.membershipId} className="order-row static member-row">
                <span className="order-title">{m.user.name}</span>
                <span className="meta">{m.user.email}</span>
                <span className="role-pill">{m.role}</span>
                <span className="meta">{m.source ?? "domain_join"}</span>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => {
                      void (async () => {
                        try {
                          setError(null);
                          await approve({
                            membershipId: m.membershipId,
                            idempotencyId: newIdempotencyId("approve"),
                          });
                          setMessage(`Approved ${m.user.email}`);
                        } catch (err) {
                          setError(parseConvexError(err).message);
                        }
                      })();
                    }}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      void (async () => {
                        try {
                          setError(null);
                          await reject({
                            membershipId: m.membershipId,
                            idempotencyId: newIdempotencyId("reject"),
                          });
                          setMessage(`Rejected ${m.user.email}`);
                        } catch (err) {
                          setError(parseConvexError(err).message);
                        }
                      })();
                    }}
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="order-section">
        <h2>Open invites</h2>
        <p className="muted">
          Waiting for the invitee to open the link and sign in with Google as that email. Access
          activates automatically on accept.
        </p>
        {(openInvites ?? []).length === 0 ? (
          <p className="muted">No open invites.</p>
        ) : (
          <ul className="order-list">
            {(openInvites ?? []).map((inv) => (
              <li key={inv.inviteId} className="order-row static member-row">
                <span className="order-title">{inv.email}</span>
                <span className="role-pill">{inv.role}</span>
                <span className="meta">expires {formatRelativeTime(inv.expiresAt)}</span>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      void copyText(inviteLink(inv.token), `Invite link copied for ${inv.email}.`)
                    }
                  >
                    Copy invite link
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      void (async () => {
                        try {
                          setError(null);
                          await revokeInvite({
                            inviteId: inv.inviteId,
                            idempotencyId: newIdempotencyId("revoke-invite"),
                          });
                          setMessage(`Revoked invite for ${inv.email}`);
                        } catch (err) {
                          setError(parseConvexError(err).message);
                        }
                      })();
                    }}
                  >
                    Revoke
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="order-section">
        <h2>Access attempts</h2>
        <p className="muted">
          Sign-ins that did not get access (off-domain, rejected, or missing verified email).
        </p>
        {(accessAttempts ?? []).length === 0 ? (
          <p className="muted">No open access attempts.</p>
        ) : (
          <ul className="order-list">
            {(accessAttempts ?? []).map((a) => (
              <li key={a._id} className="order-row static member-row">
                <span className="order-title">{a.email ?? a.name ?? a.workosUserId}</span>
                <span className="meta">
                  {a.domain ? `@${a.domain}` : "no domain"} · {a.reason} · {a.attemptCount} attempt
                  {a.attemptCount === 1 ? "" : "s"} · last {formatRelativeTime(a.lastAttemptAt)}
                </span>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={!a.email}
                    onClick={() => {
                      if (a.email) setInviteEmail(a.email);
                      setMessage(
                        a.email
                          ? `Invite form prefilled with ${a.email}. Add their domain if needed, then Invite.`
                          : null,
                      );
                    }}
                  >
                    Invite
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      void (async () => {
                        try {
                          setError(null);
                          await dismissAttempt({
                            attemptId: a._id,
                            idempotencyId: newIdempotencyId("dismiss-attempt"),
                          });
                          setMessage(`Dismissed attempt for ${a.email ?? a.workosUserId}`);
                        } catch (err) {
                          setError(parseConvexError(err).message);
                        }
                      })();
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="order-section">
        <h2>Invite teammate</h2>
        <p className="muted">
          Email must be on an approved domain. Creates a Track invite link
          {workOsApi?.canSendInviteEmail
            ? "; WorkOS will also email the invitation when possible."
            : ". Without WORKOS_API_KEY, copy and share the invite link."}
        </p>
        <div className="toolbar">
          <input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@company.com"
            aria-label="Invite email"
          />
          <select
            value={inviteRole}
            aria-label="Role"
            onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              void (async () => {
                try {
                  await sendInvite(inviteEmail, inviteRole);
                } catch (err) {
                  setError(parseConvexError(err).message);
                }
              })();
            }}
          >
            Invite
          </button>
        </div>
        {lastInviteLink ? (
          <div className="toolbar">
            <input
              readOnly
              value={lastInviteLink}
              aria-label="Invite link"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="btn"
              onClick={() => void copyText(lastInviteLink, "Invite link copied.")}
            >
              Copy invite link
            </button>
          </div>
        ) : null}
      </section>

      <section className="order-section">
        <h2>Active members</h2>
        <ul className="order-list">
          {active.map((m) => (
            <li key={m.membershipId} className="order-row static member-row">
              <span className="order-title">{m.user.name}</span>
              <span className="meta">
                {m.user.email}
                {m.source ? ` · ${m.source}` : ""}
              </span>
              <select
                aria-label={`Role for ${m.user.email}`}
                value={m.role}
                onChange={(e) => {
                  const role = e.target.value as "admin" | "editor" | "viewer";
                  void (async () => {
                    try {
                      setError(null);
                      await updateRole({
                        membershipId: m.membershipId,
                        role,
                        idempotencyId: newIdempotencyId("role"),
                      });
                    } catch (err) {
                      setError(parseConvexError(err).message);
                    }
                  })();
                }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="button"
                className="btn"
                disabled={m.user.id === me.user.id}
                onClick={() => {
                  void (async () => {
                    try {
                      setError(null);
                      await removeMember({
                        membershipId: m.membershipId,
                        idempotencyId: newIdempotencyId("remove"),
                      });
                      setMessage(`Removed ${m.user.email}`);
                    } catch (err) {
                      setError(parseConvexError(err).message);
                    }
                  })();
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>

        {rejected.length > 0 && (
          <>
            <h3>Rejected</h3>
            <ul className="order-list">
              {rejected.map((m) => (
                <li key={m.membershipId} className="order-row static">
                  <span className="order-title">{m.user.name}</span>
                  <span className="meta">{m.user.email}</span>
                  <span className="meta">rejected</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="order-section">
        <h2>Approved domains</h2>
        <p className="muted">
          Required to invite teammates and to approve domain-join requests. Sign-ins from an
          approved domain create a pending viewer for Approve; invites auto-activate on accept.
        </p>
        <ul className="order-list">
          {(domains ?? []).map((d) => (
            <li key={d._id} className="order-row static member-row">
              {editingDomainId === d._id ? (
                <>
                  <input
                    value={editingDomainValue}
                    onChange={(e) => setEditingDomainValue(e.target.value)}
                    aria-label="Edit domain"
                  />
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => {
                      void (async () => {
                        try {
                          setError(null);
                          await editDomain({
                            domainId: d._id,
                            domain: editingDomainValue,
                            idempotencyId: newIdempotencyId("domain-edit"),
                          });
                          setEditingDomainId(null);
                        } catch (err) {
                          setError(parseConvexError(err).message);
                        }
                      })();
                    }}
                  >
                    Save
                  </button>
                  <button type="button" className="btn" onClick={() => setEditingDomainId(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="order-title">@{d.domain}</span>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setEditingDomainId(d._id);
                      setEditingDomainValue(d.domain);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      void (async () => {
                        try {
                          setError(null);
                          await removeDomain({
                            domainId: d._id,
                            idempotencyId: newIdempotencyId("domain-rm"),
                          });
                        } catch (err) {
                          setError(parseConvexError(err).message);
                        }
                      })();
                    }}
                  >
                    Remove
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
        <div className="toolbar">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="company.com"
          />
          <button
            type="button"
            className="btn"
            onClick={() => {
              void (async () => {
                try {
                  setError(null);
                  await addDomain({
                    domain,
                    idempotencyId: newIdempotencyId("domain"),
                  });
                  setDomain("");
                } catch (err) {
                  setError(parseConvexError(err).message);
                }
              })();
            }}
          >
            Add domain
          </button>
        </div>
      </section>

      <section className="order-section">
        <h2>User audit log</h2>
        <ul className="order-list audit-list">
          {(userAudit ?? []).map((e) => (
            <li key={e._id} className="order-row static">
              <span className="op">{e.operation}</span>
              <span className="meta">
                {e.actorName}
                {e.targetEmail ? ` → ${e.targetEmail}` : ""}
              </span>
              <span className="time">{formatRelativeTime(e.timestamp)}</span>
            </li>
          ))}
          {(userAudit ?? []).length === 0 && <p className="muted">No user admin events yet.</p>}
        </ul>
      </section>

      <section className="order-section">
        <h2>Backup / export</h2>
        <button
          type="button"
          className="btn primary"
          disabled={!backup}
          onClick={() => {
            if (!backup) return;
            downloadTextFile(
              `track-backup-${backup.organization.slug}.json`,
              JSON.stringify(backup, null, 2),
              "application/json",
            );
          }}
        >
          Download backup JSON
        </button>
      </section>

      <section className="order-section">
        <h2>Dev bootstrap</h2>
        <p className="muted">
          Only works when Convex env <code>ALLOW_DEV_BOOTSTRAP=true</code>.
        </p>
        <button
          type="button"
          className="btn"
          onClick={() => {
            void (async () => {
              try {
                setError(null);
                const result = await seed({});
                setMessage(result.message);
              } catch (err) {
                setError(parseConvexError(err).message);
              }
            })();
          }}
        >
          Seed sample data
        </button>
      </section>

      {message && <p className="banner">{message}</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
